import React, { useState } from 'react';
import { ChevronRight, Info, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import ImageLightbox, { ZoomableImage } from '../../shared/ImageLightbox';

// Import screenshots
import screenshot1 from '../../../assets/screenshots/thi-ooor/thi-ooor-1.png';
import screenshot2 from '../../../assets/screenshots/thi-ooor/thi-ooor-2.png';
import screenshot3 from '../../../assets/screenshots/thi-ooor/thi-ooor-3.png';
import screenshot4 from '../../../assets/screenshots/thi-ooor/thi-ooor-4.png';
import screenshot5 from '../../../assets/screenshots/thi-ooor/thi-ooor-5.png';
import screenshot6 from '../../../assets/screenshots/thi-ooor/thi-ooor-6.png';
import screenshot7 from '../../../assets/screenshots/thi-ooor/thi-ooor-7.png';
import screenshot8 from '../../../assets/screenshots/thi-ooor/thi-ooor-8.png';
import screenshot9 from '../../../assets/screenshots/thi-ooor/thi-ooor-9.png';

const OOORProcedures = () => {
  const [open, setOpen] = useState(() => new Set(['find-patient']));
  const [zoom, setZoom] = useState(null);

  const toggleSection = (section) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <p className="px-1 text-sm text-gray-600 dark:text-gray-300">
        For documenting airways, codes and other procedures performed outside the OR.
      </p>

      {/* Step 1: Finding the Patient */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('find-patient')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>1</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Find the Patient in Epic</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Use Patient Station or Patient Lists</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('find-patient') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('find-patient') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            {/* Critical Warning about Cath Lab Status Board */}
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <XCircle size={24} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-red-900 dark:text-red-200 mb-2">CRITICAL: Do NOT Use Cath Lab Status Board!</h4>
                  <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed">
                    For Cath Lab intubations, <strong>never</strong> open the patient chart through the Cath Lab Status Board. This incorrectly links your intubation encounter with the procedural encounter, making it appear that anesthesia was responsible for sedation during the entire case. Always use Patient Station or Patient Lists as described below.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Initial Steps:</h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">1.</span>
                  <span>After performing the procedure, grab a patient sticker from their chart</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">2.</span>
                  <span>Log onto Epic and find the patient using one of two methods:</span>
                </li>
              </ol>

              <div className="mt-4 space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Method 1: Patient Station</p>
                  <p className="text-sm text-gray-900 dark:text-white">Use this if you have the patient's MRN</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Method 2: Patient Lists</p>
                  <p className="text-sm text-gray-900 dark:text-white">Use this to browse inpatients by unit</p>
                </div>
              </div>
            </div>

            <ZoomableImage src={screenshot1} alt="Patient Station and Patient Lists options in Epic" caption="Click Patient Station (if you have the MRN) or Patient Lists to find the patient in Epic" onZoom={setZoom} />

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">If Using Patient Lists:</h4>
              <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li className="flex gap-2">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0]">1.</span>
                  <span>Scroll down to <strong>SLEH</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0]">2.</span>
                  <span>Click <strong>Units</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0]">3.</span>
                  <span>Navigate to the unit where your patient is located</span>
                </li>
              </ol>
            </div>

            <ZoomableImage src={screenshot2} alt="Selecting SLEH and Units in Patient Lists" caption="Click Units under SLEH, then navigate to the unit you need" onZoom={setZoom} />
          </div>
        )}
      </div>

      {/* Step 2: Opening the Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('open-chart')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>2</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Open Chart & Create New Encounter</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Click current admission, then create anesthetic record</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('open-chart') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('open-chart') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Opening the Chart:</h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">1.</span>
                  <span>Open the chart by clicking the <strong>Current Admission</strong> (shown in green)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">2.</span>
                  <span>Create a new encounter of the appropriate type (intubation, line placement, etc.)</span>
                </li>
              </ol>
            </div>

            <ZoomableImage src={screenshot3} alt="Encounter screen showing current admission" caption="Click the current admission (in green) to proceed" onZoom={setZoom} />

            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-3 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900 dark:text-white">
                  <strong>Important:</strong> It is critical that you create the encounter this way (by clicking the hospital admission first). This ensures proper documentation structure.
                </p>
              </div>
            </div>

            <ZoomableImage src={screenshot4} alt="Creating new anesthetic record for procedure" caption="Select the appropriate procedure type (intubation, line placement, etc.)" onZoom={setZoom} />
          </div>
        )}
      </div>

      {/* Step 3: Completing the Anesthetic Record */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('anesthetic-record')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>3</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Complete the Anesthetic Record</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Only fill out specific sections</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('anesthetic-record') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('anesthetic-record') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-4 rounded">
              <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <CheckCircle size={20} />
                Complete These Sections (Blue Boxes):
              </h4>
              <ul className="space-y-2 text-sm text-gray-900 dark:text-white">
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>Staff:</strong> Includes you and the CV Anesthesia attending (or fellow; see Step 6 below))</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>LDAs (Lines, Drains, Airways):</strong> Add endotracheal tube via Airway button, or any lines via Lines and Drains button</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>Procedure Note:</strong> A written note is <strong>required</strong> (see Step 4 below)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>Medications (Optional):</strong> Can be documented by you OR by the bedside RN - coordinate with the nurse</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-4">
              <h4 className="font-bold text-red-900 dark:text-red-200 mb-3 flex items-center gap-2">
                <XCircle size={20} />
                DO NOT Do These Things (Red Boxes):
              </h4>
              <ul className="space-y-2 text-sm text-red-900 dark:text-red-200">
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>DO NOT</strong> place any events on the record (e.g., Anesthesia Start/Stop, Induction, Intubation, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>DO NOT</strong> create a preop note ("Pre-procedure evaluation note")</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span><strong>DO NOT</strong> use other buttons like Check, Attest, Position, Orders, etc.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold flex-shrink-0">•</span>
                  <span>Logging vital signs is optional but not mandatory</span>
                </li>
              </ul>
            </div>

            <ZoomableImage src={screenshot5} alt="Anesthetic record showing what to fill out" caption="Complete the areas in blue boxes. No need to complete the areas in red boxes." onZoom={setZoom} />

            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-3 rounded">
              <div className="flex items-start gap-2">
                <Info size={18} className="text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900 dark:text-white">
                  <strong>Note:</strong> For weekend procedures when no attending is in-house, see Step 6 below for special instructions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 4: Procedure Note */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('procedure-note')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>4</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Create a Procedure Note</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Use SmartPhrase or CV Anes template</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('procedure-note') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('procedure-note') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-4 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900 dark:text-white">
                  <strong>The procedure note is the most important part of the documentation!</strong> Please use a template and have your attending co-sign the note.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Creating the Note:</h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">1.</span>
                  <span>Navigate to the <strong>Notes</strong> tab and click <strong>New Note</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">2.</span>
                  <span>The note type should be <strong>"Procedures"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">3.</span>
                  <span>Use a template - you have two options:</span>
                </li>
              </ol>

              <div className="mt-4 ml-8 space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Option 1: SmartPhrase</p>
                  <p className="text-sm text-gray-900 dark:text-white">Use your own SmartPhrase, or "steal" one from another user (e.g., Dr. Powell)</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Option 2: CV Anes SmartText</p>
                  <p className="text-sm text-gray-900 dark:text-white">Search "CV Anes" in the SmartText field to see available templates</p>
                </div>
              </div>

              <ol start="4" className="space-y-3 text-sm text-gray-700 dark:text-gray-300 mt-4">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">4.</span>
                  <span>Check <strong>"Cosign Required"</strong> and enter your attending's name</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">5.</span>
                  <span>Sign and file the note</span>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Step 5: Billing Sheet */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('billing')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>5</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Complete Billing Sheet</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Place in CV Anesthesia box in Blue Room</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('billing') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('billing') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-4 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900 dark:text-white">
                  <strong>Every OOOR procedure requires a billing sheet!</strong> This includes intubations, arterial lines, central lines, daily acute pain rounding, etc. The billing department needs this to find the anesthetic record.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Steps:</h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">1.</span>
                  <span>Navigate to the <strong>Post</strong> tab and sign the anesthetic record</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">2.</span>
                  <span>Fill out a billing sheet with the procedure details</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">3.</span>
                  <span>Attach the patient sticker you grabbed earlier</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">4.</span>
                  <span>Place the billing sheet in the CV Anesthesia box in the <strong>Blue Room</strong></span>
                </li>
              </ol>
            </div>

            <ZoomableImage src={screenshot6} alt="Example billing sheet" caption="Example billing sheet. Billing sheets are kept in the 3rd cabinet above the shelf." onZoom={setZoom} />

            <ZoomableImage src={screenshot7} alt="CV Anesthesia forms box location" caption="CV Anesthesia Forms Box, located in the Blue Room" onZoom={setZoom} />
          </div>
        )}
      </div>

      {/* Step 6: Weekend Procedures */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('weekend')}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>6</span>
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">Weekend Procedures (Special Case)</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">When supervised by moonlighting fellow</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              open.has('weekend') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('weekend') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-4 rounded">
              <h4 className="font-bold text-gray-900 dark:text-white mb-2">Weekends with Moonlighting Fellow as Attending:</h4>
              <p className="text-sm text-gray-900 dark:text-white leading-relaxed">
                On weekends, you may perform OOOR procedures while a fellow is moonlighting as your attending. <strong>The attending anesthesiologist may not be in the hospital.</strong>
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Special Instructions:</h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">1.</span>
                  <span>In the <strong>Staff</strong> section, only list yourself and the moonlighting fellow</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">2.</span>
                  <span><strong>Do NOT</strong> list the attending anesthesiologist as part of the staff, since they were not present for the procedure</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#8a7a61] dark:text-[#C4B5A0] flex-shrink-0">3.</span>
                  <span>Epic won't let you close the record without an attending. To circumvent this:</span>
                </li>
              </ol>

              <div className="mt-4 ml-8 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Workaround to Close the Chart:</p>
                <ol className="space-y-2 text-sm text-gray-900 dark:text-white">
                  <li>• Go to <strong>Events</strong></li>
                  <li>• Click the <strong>Cardiac 2</strong> tab</li>
                  <li>• Add the <strong>"No Cosign Needed"</strong> event</li>
                  <li>• This allows you to close the anesthetic record</li>
                </ol>
              </div>
            </div>

            <ZoomableImage src={screenshot8} alt="Adding No Cosign Needed event" caption={'Adding the "No Cosign Needed" event allows you to close the record when the attending was not present'} onZoom={setZoom} />
          </div>
        )}
      </div>

      {/* Critical: Cath Lab Intubations */}
      <div className="bg-white rounded-xl border-2 border-red-300 dark:border-red-700 overflow-hidden">
        <button
          onClick={() => toggleSection('cath-lab')}
          className="w-full p-4 flex items-center justify-between bg-red-50 hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 text-white rounded-lg">
              <XCircle size={20} />
            </div>
            <div className="text-left">
              <div className="font-bold text-red-900 dark:text-red-200">CRITICAL: Cath Lab Intubations</div>
              <div className="text-sm text-red-700 dark:text-red-300 mt-0.5">Never use the Cath Lab Status Board!</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-red-600 dark:text-red-400 transition-transform ${
              open.has('cath-lab') ? 'rotate-90' : ''
            }`}
          />
        </button>

        {open.has('cath-lab') && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-800/60 space-y-4">
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <XCircle size={24} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-red-900 dark:text-red-200 mb-2 text-lg">Common Mistake - DO NOT DO THIS!</h4>
                  <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed mb-3">
                    Sometimes we are called to emergently intubate a patient in the Cath Lab who is undergoing a procedure under moderate sedation by the cardiologist. When documenting the intubation, <strong>one common mistake is that residents will open the chart by going to the Cath Lab Status Board.</strong>
                  </p>
                  <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed">
                    <strong>This is incorrect!</strong> Using the Status Board links the Anesthetic Record for the intubation with the procedure itself, making it seem as if anesthesia was responsible for the sedation for the entire case. This causes confusion later on.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-[#9F8D70] p-4 rounded">
              <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <CheckCircle size={20} />
                Correct Method for Cath Lab Intubations:
              </h4>
              <ol className="space-y-2 text-sm text-gray-900 dark:text-white ml-4">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>Find the patient using <strong>Patient Station</strong> (NOT the Status Board)</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Click on the <strong>current admission</strong> (which should be green)</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>Create a <strong>separate encounter</strong> for the intubation (as in Step 2 above)</span>
                </li>
              </ol>
              <p className="text-sm text-gray-900 dark:text-white mt-3 font-semibold">
                The same applies for any patient you intubate who is undergoing a procedure not actively under the care of an anesthesiologist (e.g., Interventional Radiology, etc.)
              </p>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <button type="button" onClick={() => setZoom({ src: screenshot9, alt: 'Cath Lab Status Board - do not use' })} className="block w-full" aria-label="Enlarge screenshot">
                  <img
                    src={screenshot9}
                    alt="Cath Lab Status Board - do not use"
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600"
                  />
                </button>
                {/* CSS Red X Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{ opacity: 0.8 }}
                  >
                    <line
                      x1="0" y1="0" x2="100" y2="100"
                      stroke="#dc2626"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                    <line
                      x1="100" y1="0" x2="0" y2="100"
                      stroke="#dc2626"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-red-600 dark:text-red-400 font-bold italic text-center">
                DO NOT use the Cath Lab Status Board to open the patient chart for intubations!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CheckCircle size={20} />
          Quick Summary
        </h3>
        <ol className="space-y-1 text-sm text-gray-900 dark:text-white">
          <li>1. Find patient via Patient Station or Patient Lists (NOT Status Board for Cath Lab)</li>
          <li>2. Click current admission → Create new encounter</li>
          <li>3. Fill out: Staff, LDAs, Procedure Note (DO NOT place events or hit Anesthesia Start/Stop)</li>
          <li>4. Create procedure note in Notes tab with cosign</li>
          <li>5. Sign record & complete billing sheet → Place in Blue Room box</li>
          <li>6. Weekend only: Use "No Cosign Needed" event if fellow was moonlighting</li>
        </ol>
      </div>

      <p className="px-1 text-xs text-gray-400 dark:text-gray-500">Questions: contact the rotation site director.</p>

      <ImageLightbox image={zoom} onClose={() => setZoom(null)} />
    </div>
  );
};

export default OOORProcedures;
